import React, { useState, useEffect, useCallback } from 'react';
import { Heart, Sparkles, Check, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import client from '../api/client';

const DISMISSED_KEY = 'thankyou_card_dismissed';

const ThankYouCard = () => {
  const { user, setUser } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  useEffect(() => {
    if (
      user &&
      user.seen_thank_you_card === false &&
      !localStorage.getItem(DISMISSED_KEY)
    ) {
      setVisible(true);
      // Trigger entrance animation after mount
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });
    }
  }, [user?.id]); // Only check once per user session, not on every user object change

  // Listen for manual trigger from notification click
  useEffect(() => {
    const show = () => {
      setAnimateOut(false);
      setAnimateIn(false);
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });
    };
    window.addEventListener('show-thankyou-card', show);
    return () => window.removeEventListener('show-thankyou-card', show);
  }, []);

  const handleDismiss = useCallback(async () => {
    // Immediately mark in localStorage so it never shows again even if API fails
    localStorage.setItem(DISMISSED_KEY, 'true');
    setAnimateOut(true);
    // Wait for exit animation
    setTimeout(() => setVisible(false), 400);
    try {
      await client.patch('/users/me', { seen_thank_you_card: true });
      setUser(prev => ({ ...prev, seen_thank_you_card: true }));
    } catch { /* localStorage fallback already covers this */ }
  }, [setUser]);

  const proFeatures = [
    { icon: '💪', text: t('pro.feature_supplements', 'Full Supplements tracking') },
    { icon: '🥗', text: t('pro.feature_diet', 'Complete Diet & Nutrition') },
    { icon: '📋', text: t('pro.feature_routines', 'Unlimited routines') },
    { icon: '📸', text: t('pro.feature_checkins', 'Unlimited check-ins') },
    { icon: '🏋️', text: t('pro.feature_exercises', 'Unlimited custom exercises') },
    { icon: '⭐', text: t('pro.feature_badge', 'PRO badge on social') },
  ];

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-all duration-500 ease-out ${
        animateOut ? 'opacity-0 scale-95' : animateIn ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        background: isDark
          ? 'linear-gradient(165deg, #1a1a1a 0%, #0d0d0d 40%, #1a0a00 70%, #0d0d0d 100%)'
          : 'linear-gradient(165deg, #fafafa 0%, #f4f4f5 40%, #fff7ee 70%, #fafafa 100%)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Animated background glow */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        aria-hidden="true"
      >
        <div
          className={`absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full ${isDark ? 'opacity-20' : 'opacity-15'}`}
          style={{
            background: 'radial-gradient(circle, #FF5C00 0%, transparent 70%)',
            animation: 'pulseGlow 4s ease-in-out infinite',
          }}
        />
        <div
          className={`absolute bottom-1/4 left-1/2 -translate-x-1/2 translate-y-1/2 w-[300px] h-[300px] rounded-full ${isDark ? 'opacity-10' : 'opacity-8'}`}
          style={{
            background: 'radial-gradient(circle, #FF5C00 0%, transparent 70%)',
            animation: 'pulseGlow 4s ease-in-out infinite 2s',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-sm px-6">
        {/* Crown icon with animated ring */}
        <div
          className={`relative mb-6 transition-all duration-700 delay-200 ${
            animateIn ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-75'
          }`}
        >
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C42 100%)',
              boxShadow: '0 0 40px rgba(255, 92, 0, 0.4), 0 0 80px rgba(255, 92, 0, 0.15)',
            }}
          >
            <Crown className="w-10 h-10 text-white" strokeWidth={1.8} />
          </div>
          {/* Sparkle accents */}
          <Sparkles
            className="absolute -top-2 -right-2 w-5 h-5 text-primary"
            style={{ animation: 'sparkle 2s ease-in-out infinite' }}
          />
          <Sparkles
            className="absolute -bottom-1 -left-3 w-4 h-4 text-orange-400/60"
            style={{ animation: 'sparkle 2s ease-in-out infinite 0.8s' }}
          />
        </div>

        {/* Title */}
        <h1
          className={`text-3xl font-black text-zinc-900 dark:text-white mb-3 tracking-tight transition-all duration-700 delay-300 ${
            animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          {t('thankyou.title', 'Thank You!')}
        </h1>

        {/* Message */}
        <p
          className={`text-sm text-zinc-600 dark:text-gray-400 text-center leading-relaxed mb-8 max-w-[280px] transition-all duration-700 delay-[400ms] ${
            animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {t(
            'thankyou.message',
            "Thank you for being an early adopter and helping us test the app. As a token of our gratitude, you've been granted 1 year of free Pro access!"
          )}
        </p>

        {/* PRO badge */}
        <div
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full mb-6 transition-all duration-700 delay-500 ${
            animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(255,92,0,0.2) 0%, rgba(255,140,66,0.1) 100%)',
            border: '1px solid rgba(255,92,0,0.3)',
          }}
        >
          <Sparkles className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-bold text-primary uppercase tracking-wider">
            {t('thankyou.pro_includes', 'Your Pro includes')}
          </span>
        </div>

        {/* Feature list */}
        <div
          className={`w-full space-y-3 mb-10 transition-all duration-700 delay-[600ms] ${
            animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {proFeatures.map((f, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
              style={{
                background: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
                border: isDark ? undefined : '1px solid rgba(0,0,0,0.06)',
                boxShadow: isDark ? undefined : '0 1px 2px rgba(0,0,0,0.04)',
                backdropFilter: 'blur(8px)',
                transitionDelay: `${650 + i * 60}ms`,
              }}
            >
              <span className="text-base flex-shrink-0">{f.icon}</span>
              <span className="text-sm text-zinc-700 dark:text-gray-300">{f.text}</span>
              <Check className="w-4 h-4 text-green-700 dark:text-green-400/80 ml-auto flex-shrink-0" />
            </div>
          ))}
        </div>

        {/* CTA button */}
        <button
          onClick={handleDismiss}
          className={`w-full py-4 rounded-2xl text-white font-bold text-base tracking-wide active:scale-[0.97] transition-all duration-700 delay-[900ms] ${
            animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{
            background: 'linear-gradient(135deg, #FF5C00 0%, #FF8C42 100%)',
            boxShadow: '0 4px 24px rgba(255, 92, 0, 0.4), 0 1px 3px rgba(255, 92, 0, 0.2)',
            fontFamily: "'Barlow Condensed', sans-serif",
          }}
        >
          {t('thankyou.got_it', 'Got it!')}
        </button>

        {/* Heart footer */}
        <div
          className={`mt-6 flex items-center gap-1.5 transition-all duration-700 delay-[1000ms] ${
            animateIn ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <Heart className="w-3 h-3 text-primary" fill="#FF5C00" />
          <span className="text-[11px] text-zinc-500 dark:text-gray-600">trainedbyRS</span>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes pulseGlow {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.15; }
          50% { transform: translate(-50%, -50%) scale(1.15); opacity: 0.25; }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 0.4; transform: scale(0.8) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.1) rotate(15deg); }
        }
      `}</style>
    </div>
  );
};

export default ThankYouCard;
